import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Button from "components/button";
import { Columns, TableSortState } from "components/table";
import ActionMenu from "components/shared/action-menu";
import { useAppTranslation } from "locale/useAppTranslation";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { DriverRow, DriverStatus } from "_interfaces/driver.interfaces";
import {
  useDriverServiceV1GetDriversQuery,
  useDriverServiceV1ImportDriverMutation,
  useDriverServiceV1ExportDriversMutation,
  useDriverServiceV1BulkUpdateDriverStatusMutation,
  useDriverServiceV1BulkDeleteDriverMutation,
  TransportResourceImportDriverError,
} from "_services/modules/transport-resource/driver";
import { detailDriverPageRouteName } from "../detail/detail.page";
import { errorHandler } from "_services/errorHandler";
import {
  editDriverPageRouteName,
  addDriverPageRouteName,
} from "../constants/routes";
import { env } from "config/env";
import { dateTimeFormatter } from "_helper/timeFormatter";
const useDriverList = () => {
  const { t } = useAppTranslation("driver");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [selectedKeys, setSelectedKeys] = useState<(string | number)[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortState, setSortState] = useState<TableSortState>(null);
  const [bulkAction, setBulkAction] = useState("");
  const [selectedDriver, setSelectedDriver] = useState<DriverRow | null>(null);
  const [lastDeletedDriver, setLastDeletedDriver] = useState<DriverRow | null>(null);
  const [lastDeletedCount, setLastDeletedCount] = useState<number>(0);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleteSuccessOpen, setIsDeleteSuccessOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [selectedImportFile, setSelectedImportFile] = useState<File | null>(
    null
  );
  const [importValidationErrors, setImportValidationErrors] = useState<
    TransportResourceImportDriverError[]
  >([]);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [fileFormat, setFileFormat] = useState("xlsx");
  const [exportType, setExportType] = useState("all");

  const sortByMap: Record<string, string> = {
    driverName: "full_name",
    vendor: "vendor_name",
    phone: "phone_number",
    status: "status",
    licenseType: "license_type",
    createdBy: "created_at",
    updatedBy: "updated_at",
  };
  const [isDeactivateOpen, setIsDeactivateOpen] = useState(false);
  const [deactivateTargetDriver, setDeactivateTargetDriver] = useState<DriverRow | null>(null);

  const { data: apiResponse, isLoading, isFetching, refetch } = useDriverServiceV1GetDriversQuery({
    page: currentPage,
    limit: rowsPerPage,
    search,
    status: statusFilter || "available,assigned,inactive",
    sortBy: sortState ? sortByMap[sortState.columnId] : "created_at",
    order: sortState?.direction ?? "desc",
  });
  const { data: draftResponse } = useDriverServiceV1GetDriversQuery({
    status: "Draft",
    limit: 1,
  });
  const draftCount = draftResponse?.metadata?.total ?? 0;
  const [importDriver, { isLoading: isImportLoading }] =
    useDriverServiceV1ImportDriverMutation();
  const [exportDrivers] = useDriverServiceV1ExportDriversMutation();
  const [bulkUpdateStatus, { isLoading: isUpdatingStatus }] = useDriverServiceV1BulkUpdateDriverStatusMutation();
  const [bulkDeleteDriver, { isLoading: isDeleting }] =
    useDriverServiceV1BulkDeleteDriverMutation();

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-list"),
        hasBackButton: false,
      })
    );
  }, [dispatch, t]);

  const tableData: DriverRow[] = useMemo(() => {
    if (!apiResponse?.driver) {
      return [];
    }
    return apiResponse.driver
      .filter((item: any) => item.status?.toLowerCase() !== "draft")
      .map((item) => {
        const isUpdated = Boolean(
          item.updated_by &&
          (item.updated_by.name || item.updated_by.timestamp) &&
          item.updated_by.name !== "-",
        );

        return {
          id: item.id ?? "",
          driverName: item.full_name ?? "-",
          driverCode: item.id ?? "-",
          vendor: item.vendor_name ?? "-",
          vendorCode: item.vendor_code ?? "-",
          driverPictureUrl: item.driver_picture_url ?? "",
          phone: item.phone_number ?? "-",
          status: (item.status?.toLowerCase() ?? "inactive") as DriverStatus,
          licenseType: item.license_type ?? "-",
          licenseNumber: item.license_number ?? "-",
          createdBy: {
            name: item.created_by?.name ?? "-",
            timestamp: item.created_by?.timestamp
              ? dateTimeFormatter(item.created_by.timestamp)
              : "-",
          },
          updatedBy: {
            name: item.updated_by?.name || item.created_by?.name || "-",
            timestamp: item.updated_by?.timestamp
              ? dateTimeFormatter(item.updated_by.timestamp)
              : item.created_by?.timestamp
              ? dateTimeFormatter(item.created_by.timestamp)
              : "-",
          },
          rating: "5.0",
        };
      });
  }, [apiResponse]);

  const handleDeleteClick = (row: DriverRow) => {
    const status = row.status?.toLowerCase();
    if (status !== "inactive" && status !== "draft") {
      toast.error(
        t("toast-cannot-delete-driver-status") ||
          "Only inactive drivers can be deleted"
      );
      return;
    }
    setSelectedDriver(row);
    setIsDeleteOpen(true);
  };

  const handleConfirmDelete = async (reason: string) => {
    const idsToDelete = selectedDriver
      ? [selectedDriver.id]
      : (selectedKeys as string[]);

    if (idsToDelete.length === 0) {
      return;
    }

    const isSingle = Boolean(selectedDriver);
    const driver = selectedDriver;
    const count = idsToDelete.length;

    try {
      await bulkDeleteDriver({
        ids: idsToDelete,
        reason,
      }).unwrap();
      setLastDeletedDriver(driver);
      setLastDeletedCount(count);
      setIsDeleteOpen(false);
      setIsDeleteSuccessOpen(true);
      if (!isSingle) {
        setSelectedKeys([]);
        setBulkAction("");
      }
      setSelectedDriver(null);
      await refetch();
    } catch {
      toast.error("Failed to delete driver");
    }
  };

  const handleConfirmDeactivate = async (reason: string) => {
    const idsToDeactivate = deactivateTargetDriver
      ? [deactivateTargetDriver.id]
      : (selectedKeys as string[]);

    if (idsToDeactivate.length === 0) return;

    try {
      await bulkUpdateStatus({
        transportResourceBulkUpdateDriverStatusReq: {
          ids: idsToDeactivate,
          status: "inactive",
        },
      }).unwrap();
      toast.success("Driver(s) deactivated successfully");
      setIsDeactivateOpen(false);
      setDeactivateTargetDriver(null);
      setSelectedKeys([]);
      setBulkAction("");
      await refetch();
    } catch {
      toast.error("Failed to deactivate driver(s)");
    }
  };

  const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);

  const selectedStatuses = useMemo(() => {
    const selectedRows = tableData.filter((row) => selectedKeys.includes(row.id));
    return Array.from(
      new Set(
        selectedRows.map((row) => {
          const s = row.status?.toLowerCase();
          if (s === "available" || s === "active") return "active";
          return s || "inactive";
        })
      )
    );
  }, [selectedKeys, tableData]);

  const bulkActionOptions = useMemo(() => {
    if (selectedKeys.length === 0) return [];

    const hasActive = selectedStatuses.includes("active");
    const hasInactive = selectedStatuses.includes("inactive");

    if (selectedStatuses.length === 1) {
      if (hasActive) {
        return [
          { label: t("bulk-deactivate") || "Deactivate", value: "deactivate" },
        ];
      }
      if (hasInactive) {
        return [
          { label: t("bulk-activate") || "Activate", value: "activate" },
          { label: t("bulk-delete") || "Delete", value: "delete" },
        ];
      }
    }

    return [];
  }, [selectedKeys.length, selectedStatuses, t]);

  const effectiveBulkAction = bulkActionOptions.some((opt) => opt.value === bulkAction) ? bulkAction : "";

  const selectedDriverNames = useMemo(() => {
    return selectedKeys
      .map((key) => {
        const row = tableData.find((r) => r.id === key);
        return row ? row.driverName : "";
      })
      .filter(Boolean);
  }, [selectedKeys, tableData]);

  const onConfirmBulkStatusUpdate = async () => {
    if (!bulkAction) return;
    if (selectedStatuses.length > 1) return;
    try {
      if (bulkAction === "activate") {
        await bulkUpdateStatus({
          transportResourceBulkUpdateDriverStatusReq: {
            ids: selectedKeys as string[],
            status: "available",
          },
        }).unwrap();
        toast.success("Drivers activated successfully");
        setSelectedKeys([]);
        setBulkAction("");
        setShowBulkConfirmModal(false);
        await refetch();
      }
    } catch {
      toast.error("Bulk action failed");
    }
  };

  const handleApplyBulkAction = () => {
    if (!bulkAction) {
      return;
    }
    if (selectedKeys.length === 0) {
      toast.error("No driver selected");
      return;
    }
    if (selectedStatuses.length > 1) {
      return;
    }
    if (bulkAction === "activate" && selectedStatuses.includes("active")) {
      return;
    }
    if (bulkAction === "deactivate" && selectedStatuses.includes("inactive")) {
      return;
    }
    if (bulkAction === "deactivate") {
      setDeactivateTargetDriver(null);
      setIsDeactivateOpen(true);
      return;
    }
    if (bulkAction === "delete") {
      const nonDeletableDrivers = selectedKeys
        .map((key) => tableData.find((r) => r.id === key))
        .filter(
          (row) =>
            row &&
            row.status?.toLowerCase() !== "inactive" &&
            row.status?.toLowerCase() !== "draft"
        );
      if (nonDeletableDrivers.length > 0) {
        toast.error(
          t("toast-active-driver-cannot-delete") ||
            "Only inactive drivers can be deleted"
        );
        return;
      }
      setSelectedDriver(null);
      setIsDeleteOpen(true);
      return;
    }
    setShowBulkConfirmModal(true);
  };
  const handleImportSubmit = async () => {
    if (!selectedImportFile) {
      toast.error("No file selected");
      return;
    }
    const fileName = selectedImportFile.name || "";
    const fileExtension = fileName.split(".").pop()?.toLowerCase()?.trim() || "";
    const allowedExtensions = ["xlsx", "xls", "csv"];
    if (!allowedExtensions.includes(fileExtension)) {
      toast.error("Invalid file type. Please select .xlsx or .xls");
      return;
    }
    const formData = new FormData();
    formData.append("file", selectedImportFile);
    try {
      const response = await importDriver({
        apiHttpBody: formData as any,
      }).unwrap();

      if (response?.success === false && response.errors && response.errors.length > 0) {
        setImportValidationErrors(response.errors);
        toast.error("Import validation failed");
        return;
      }

      toast.success("Drivers imported successfully");
      setIsImportOpen(false);
      setSelectedImportFile(null);
      setImportValidationErrors([]);
      refetch();
    } catch (error: any) {
      const backendErrors = error?.data?.errors;
      if (Array.isArray(backendErrors)) {
        setImportValidationErrors(backendErrors);
      }
      errorHandler(error);
    }
  };
  const handleImportFileSelect = (file: File) => {
    setSelectedImportFile(file);
    setImportValidationErrors([]);
  };
  const handleImportFileRemove = () => {
    setSelectedImportFile(null);
    setImportValidationErrors([]);
  };
  const handleDownloadTemplate = () => {
    if (env.DRIVER_IMPORT_TEMPLATE_URL) {
      window.open(env.DRIVER_IMPORT_TEMPLATE_URL, "_blank");
    } else {
      toast.error("Template URL is not configured");
    }
  };
  const handleExportSubmit = async () => {
    if (!fileFormat) {
      toast.error("Please select file format");
      return;
    }
    if (!exportType) {
      toast.error("Please select export type");
      return;
    }
    if (exportType === "selected" && selectedKeys.length === 0) {
      toast.error("No driver selected");
      return;
    }
    try {
      const blob = await exportDrivers({
        transportResourceExportDriversReq: {
          format: fileFormat,
          scope: exportType,
          ids: exportType === "selected" ? (selectedKeys as string[]) : [],
          search,
          status: statusFilter || "active,inactive",
          sortBy: sortState ? sortByMap[sortState.columnId] : "created_at",
          order: sortState?.direction ?? "desc",
        },
      }).unwrap();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `drivers.${fileFormat}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Export success");
      setIsExportOpen(false);
      setFileFormat("");
      setExportType("");
    } catch (error: any) {
      toast.error(error?.data?.message || "Export failed");
    }
  };
  const exportFileFormatOptions = [
    {
      label: "Excel (.xlsx)",
      value: "xlsx",
    },
    {
      label: "CSV (.csv)",
      value: "csv",
    },
  ];
  const exportTypeOptions = [
    {
      label: "All Data",
      value: "all",
    },
    {
      label: "Selected Data",
      value: "selected",
    },
  ];
  const totalPages = apiResponse?.metadata?.total_page ?? 1;
  const totalData = apiResponse?.metadata?.total ?? 0;
  const columns: Columns<DriverRow>[] = useMemo(
    () => [
      {
        id: "no",
        fieldId: "index",
        label: t("col-no") || "No.",
        width: "60px",
      },
      {
        id: "driverName",
        fieldId: "driverName",
        label: t("col-driver") || "Driver",
        sortable: true,
        render: (row) => (
          <div className="flex items-center gap-3">
            {row.driverPictureUrl ? (
              <img
                src={row.driverPictureUrl}
                alt={row.driverName}
                className="h-8 w-8 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700">
                {row.driverName ? row.driverName.charAt(0).toUpperCase() : "D"}
              </div>
            )}
            <span className="font-medium text-text-primary">{row.driverName}</span>
          </div>
        ),
      },
      {
        id: "vendor",
        fieldId: "vendor",
        label: t("col-vendor") || "Vendor",
        sortable: true,
        render: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-text-primary">{row.vendor}</span>
            <span className="text-xs text-text-secondary">{row.vendorCode || "-"}</span>
          </div>
        ),
      },
      {
        id: "phone",
        fieldId: "phone",
        label: t("col-phone") || "Phone Number",
        sortable: true,
      },
      {
        id: "status",
        fieldId: "status",
        label: t("col-status") || "Status",
        sortable: true,
        render: (row) => {
          const statusLower = row.status?.toLowerCase();
          const badgeClass =
            statusLower === "available" || statusLower === "active"
              ? "bg-green-100 text-green-700"
              : statusLower === "assigned"
              ? "bg-blue-100 text-blue-700"
              : "bg-gray-100 text-gray-700";

          return (
            <div
              className={`inline-flex rounded-full px-3 py-1 text-xs font-medium capitalize ${badgeClass}`}
            >
              {row.status}
            </div>
          );
        },
      },
      {
        id: "licenseType",
        fieldId: "licenseType",
        label: t("col-license-type") || "License Type",
        sortable: true,
        render: (row) => row.licenseType || "-",
      },
      {
        id: "rating",
        fieldId: "rating",
        label: t("col-rating") || "Star Rating",
        render: () => (
          <div className="flex items-center gap-1">
            <svg
              className="h-4 w-4 fill-yellow-400 text-yellow-400"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span className="text-sm font-medium text-text-primary">5.0</span>
          </div>
        ),
      },
      {
        id: "createdBy",
        fieldId: "createdBy",
        label: t("col-created-by") || "Created By",
        sortable: true,
        render: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-text-primary">{row.createdBy?.name || "-"}</span>
            <span className="text-xs text-text-secondary">{row.createdBy?.timestamp || "-"}</span>
          </div>
        ),
      },
      {
        id: "updatedBy",
        fieldId: "updatedBy",
        label: t("col-updated-by") || "Updated By",
        sortable: true,
        render: (row) => {
          const hasName = row.updatedBy?.name && row.updatedBy.name !== "-";
          const hasTimestamp = row.updatedBy?.timestamp && row.updatedBy.timestamp !== "-";
          if (!hasName && !hasTimestamp) {
            return "-";
          }
          return (
            <div className="flex flex-col">
              <span className="font-medium text-text-primary">{row.updatedBy?.name || "-"}</span>
              {hasTimestamp && (
                <span className="text-xs text-text-secondary">{row.updatedBy?.timestamp}</span>
              )}
            </div>
          );
        },
      },
      {
        id: "action",
        fieldId: "id",
        label: t("col-action"),
        render: (row) => {
          const statusLower = row.status?.toLowerCase();
          const isInactive = statusLower === "inactive";
          const isDraft = statusLower === "draft";
          const items = [
            {
              label: isInactive ? t("btn-activate") : t("btn-deactivate"),
              onClick: async (event?: React.MouseEvent) => {
                event?.stopPropagation();
                if (isInactive) {
                  try {
                    await bulkUpdateStatus({
                      transportResourceBulkUpdateDriverStatusReq: {
                        ids: [row.id],
                        status: "available",
                      },
                    }).unwrap();
                    toast.success("Driver activated successfully");
                    await refetch();
                  } catch {
                    toast.error("Failed to activate driver");
                  }
                } else {
                  setDeactivateTargetDriver(row);
                  setIsDeactivateOpen(true);
                }
              },
            },
            {
              label: t("btn-delete"),
              variant: "danger" as const,
              disabled: !isInactive && !isDraft,
              onClick: (event?: React.MouseEvent) => {
                event?.stopPropagation();
                handleDeleteClick(row);
              },
            },
          ];
          return (
            <div
              className="flex items-center gap-3"
              onClick={(event) => event.stopPropagation()}
            >
              <Button
                variant="outline"
                size="small"
                onClick={() => navigate(editDriverPageRouteName(row.id))}
              >
                {t("btn-edit")}
              </Button>
              <ActionMenu items={items} />
            </div>
          );
        },
      },
    ],
    [t, bulkUpdateStatus, navigate, refetch]
  );
  return {
    columns,
    data: tableData,
    totalData,
    totalPages,
    isLoading: isLoading || isUpdatingStatus,
    selectedKeys,
    setSelectedKeys,
    sortState,
    setSortState: (sort: TableSortState | ((prev: TableSortState) => TableSortState)) => {
      setSelectedKeys([]);
      setSortState(sort);
    },
    currentPage,
    setCurrentPage: (page: number | ((prev: number) => number)) => {
      setSelectedKeys([]);
      setCurrentPage(page);
    },
    rowsPerPage,
    setRowsPerPage: (rows: number | ((prev: number) => number)) => {
      setSelectedKeys([]);
      setRowsPerPage(rows);
    },
    search,
    setSearch: (val: string | ((prev: string) => string)) => {
      setSelectedKeys([]);
      setSearch(val);
    },
    statusFilter,
    setStatusFilter: (val: string | ((prev: string) => string)) => {
      setSelectedKeys([]);
      setStatusFilter(val);
    },
    bulkAction: effectiveBulkAction,
    setBulkAction,
    bulkActionOptions,
    handleApplyBulkAction,
    showBulkConfirmModal,
    setShowBulkConfirmModal,
    selectedDriverNames,
    onConfirmBulkStatusUpdate,
    isDeleteOpen,
    setIsDeleteOpen,
    isDeleteSuccessOpen,
    setIsDeleteSuccessOpen,
    handleConfirmDelete,
    isDeleting,
    isDeactivateOpen,
    setIsDeactivateOpen,
    handleConfirmDeactivate,
    deactivateTargetDriver,
    selectedDriver,
    setSelectedDriver,
    lastDeletedDriver,
    setLastDeletedDriver,
    lastDeletedCount,
    isImportOpen,
    setIsImportOpen,
    selectedImportFile,
    importValidationErrors,
    handleImportSubmit,
    handleImportFileSelect,
    handleImportFileRemove,
    handleDownloadTemplate,
    isImportLoading,
    onExport: () => {
      setExportType(selectedKeys.length > 0 ? "selected" : "all");
      setIsExportOpen(true);
    },
    isExportOpen,
    setIsExportOpen,
    fileFormat,
    setFileFormat,
    exportType,
    setExportType,
    handleExportSubmit,
    exportFileFormatOptions,
    exportTypeOptions,
    totalSelected: selectedKeys.length,
    isFetching,
    refetch,
    onAddDriver: () => {
      navigate(addDriverPageRouteName);
    },
    draftCount,
    onDraft: () => {
      navigate("/master-data/driver/draft");
    },
    onRowClick: (row: DriverRow) => {
      navigate(detailDriverPageRouteName(row.id));
    },
  };
};
export default useDriverList;
